import {
  Heart,
  Thermometer,
  Weight,
  Activity,
  Calculator,
  Ruler,
  Circle,
  Wind,
  Trash2,
  Gauge,
  AlertCircle,
  Droplets,
  Brain,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

export const VitalsItems = ({
  items,
  isDarkMode,
  handleVitalClick,
  onDeleteVital,
}: any) => {
  return (
    <div className="px-3 pb-3">
      <div className="flex flex-wrap gap-2 mt-2">
        {items.map((item: any, index: number) => {
          let vitalsDetails: any = null;

          // Parse payload safely
          if (item.details?.payload) {
            try {
              const payload = JSON.parse(item.details.payload);
              vitalsDetails = payload.vitalsDetails;
            } catch (e) {
              console.warn("Failed to parse vitals payload", e);
            }
          }
          if (!vitalsDetails) return null;

          const vitalLabel = vitalsDetails.vitalType || "Vital";
          let vitalValue = "";
          let vitalUnit = "";
          let vitalType = "";

          if (vitalsDetails.bmi) {
            vitalValue = vitalsDetails.bmi.Value;
            vitalUnit = vitalsDetails.bmi.Unit || "kg/m²";
            vitalType = "bmi";
          } else if (vitalsDetails.bloodPressure) {
            const bp = vitalsDetails.bloodPressure;
            vitalValue = `${bp.Systolic?.Value || "-"}/${bp.Diastolic?.Value || "-"}`;
            vitalUnit = bp.Systolic?.Unit || "mmHg";
            vitalType = "blood-pressure";
          } else if (vitalsDetails.heartRate) {
            vitalValue = vitalsDetails.heartRate.Value;
            vitalUnit = vitalsDetails.heartRate.Unit || "BPM";
            vitalType = "heart-rate";
          } else if (vitalsDetails.temperature) {
            vitalValue = vitalsDetails.temperature.Value;
            vitalUnit = vitalsDetails.temperature.Unit || "°C";
            vitalType = "temperature";
          } else if (vitalsDetails.respiratoryRate) {
            vitalValue = vitalsDetails.respiratoryRate.Value;
            vitalUnit = vitalsDetails.respiratoryRate.Unit || "breaths/min";
            vitalType = "respiratory-rate";
          } else if (vitalsDetails.circumference) {
            vitalValue = vitalsDetails.circumference.Value;
            vitalUnit = vitalsDetails.circumference.Unit || "cm";
            vitalType = "circumference";
          } else if (vitalsDetails.height) {
            vitalValue = vitalsDetails.height.Value;
            vitalUnit = vitalsDetails.height.Unit || "cm";
            vitalType = "height";
          } else if (vitalsDetails.weight) {
            vitalValue = vitalsDetails.weight.Value;
            vitalUnit = vitalsDetails.weight.Unit || "kg";
            vitalType = "weight";
          } else if (vitalsDetails.OxygenSaturation) {
            vitalValue = vitalsDetails.OxygenSaturation.Value;
            vitalUnit = vitalsDetails.OxygenSaturation.Unit || "%";
            vitalType = "oxygen-saturation";
          } else if (vitalsDetails.PainScore) {
            vitalValue = vitalsDetails.PainScore.Value;
            vitalUnit = vitalsDetails.PainScore.Unit || "/10";
            vitalType = "news-pain-score";
          } else if (vitalsDetails.GlucoseLevel) {
            vitalValue = vitalsDetails.GlucoseLevel.Value;
            vitalUnit = vitalsDetails.GlucoseLevel.Unit || "mmol/L";
            vitalType = "blood-sugar";
          } else if (vitalsDetails.Consciousness) {
            vitalValue = vitalsDetails.Consciousness.Value;
            vitalUnit = "";
            vitalType = "consciousness-level";
          } else if (vitalsDetails.SupplementalOxygen) {
            vitalValue = vitalsDetails.SupplementalOxygen.Value;
            vitalUnit = "";
            vitalType = "supplemental-oxygen";
          }
          const getVitalIcon = (type: string) => {
            const iconClass = `w-4 h-4 flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
            switch (type) {
              case "heart-rate":
                return <Heart className={iconClass} />;
              case "temperature":
                return <Thermometer className={iconClass} />;
              case "blood-pressure":
                return <Activity className={iconClass} />;
              case "height":
                return <Ruler className={iconClass} />;
              case "weight":
                return <Weight className={iconClass} />;
              case "respiratory-rate":
                return <Wind className={iconClass} />;
              case "circumference":
                return <Circle className={iconClass} />;
              case "bmi":
                return <Calculator className={iconClass} />;
              case "oxygen-saturation":
                return <Gauge className={iconClass} />;
              case "news-pain-score":
                return <AlertCircle className={iconClass} />;
              case "blood-sugar":
                return <Droplets className={iconClass} />;
              case "consciousness-level":
                return <Brain className={iconClass} />;
              case "supplemental-oxygen":
                return <Wind className={iconClass} />;
              default:
                return <Heart className={iconClass} />;
            }
          };

          return (
            <div
              key={item.vitalMessageId || index}
              className="group relative flex flex-col items-center p-2.5 rounded-xl min-w-[70px] flex-1 basis-[calc(33.333%-6px)] max-w-[calc(33.333%-6px)]"
              onClick={() => handleVitalClick(item)}
            >
              {/* Delete button */}
              <div
                className="absolute transition-opacity opacity-0 top-1 right-1 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    {/* <button
                      className={`p-0.5 rounded transition-colors ${
                        isDarkMode
                          ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                          : "hover:bg-red-50 text-red-600 hover:text-red-700"
                      }`}
                      title="Delete vital"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button> */}
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Vital Sign</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this vital sign? This
                        cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        // onClick={() => onDelete(action.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {getVitalIcon(vitalType)}
              <p
                className={`text-lg font-bold mt-1 ${isDarkMode ? "text-gray-200" : "text-gray-900"}`}
              >
                {vitalValue}
                {vitalUnit && (
                  <span
                    className={`text-xs font-normal ml-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {vitalUnit}
                  </span>
                )}
              </p>
              <p
                className={`text-xs text-center ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {vitalLabel}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
